import {
    IonBackButton,
    IonButtons,
    IonIcon,
    IonItem,
    IonRefresher,
    IonRefresherContent,
    IonText,
    RefresherEventDetail,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { call } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { useCallback, useMemo, useState } from 'react';

import LoadableContent from '../components/content/LoadableContent';
import LoadingButton from '../components/loadingbutton/LoadingButton';
import Page from '../components/page/Page';
import { IInstallationRouteProps } from '../interfaces/IRoute';
import {
    extendedJobDetailsLoadableAtom,
    jobDetailsLoadableAtom,
    startInstallationAtom,
} from '../store/Atoms';
import { isCeeEnabled, isQuoteSigned, isSocialHousing } from '../utils/cee';
import { ODOO_INST_STATES, ODOO_INST_TYPE } from '../utils/constants';
import { timestampToDate, timestampToTime } from '../utils/date';
import { useCustomRouter } from '../utils/hooks/useCustomRouter';

const JobDetails: React.FC<IInstallationRouteProps> = ({ match }) => {
    const jobId = +match.params.jobId;
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [value, refresh] = useAtom(extendedJobDetailsLoadableAtom);
    const [, refreshJobDetails] = useAtom(jobDetailsLoadableAtom);
    const [loading, setLoading] = useState(false);
    const [, startInstallation] = useAtom(startInstallationAtom);
    const job = useMemo(
        () => (value.state === 'hasData' ? value.data : undefined),
        [value]
    );
    const heatersToInstall = job?.nbHeatings || '-';
    const numOfRooms = useMemo(() => (job ? job.rooms.length : 0), [job]);
    const numOfHeaters = useMemo(
        () =>
            job
                ? job.materialIds.filter(
                      (materialId) => materialId.typeProduct === 'DBOX'
                  ).length
                : 0,
        [job]
    );
    const photosList: string[] = useMemo(
        () => (value.state === 'hasData' ? value.data.photosList : []),
        [value]
    );

    const hasQuoteSignature = useMemo(
        () => (photosList ? isQuoteSigned(photosList) : false),
        [photosList]
    );

    const onStartInterventionHandler = useCallback(async () => {
        setLoading(true);
        try {
            // We don't want to start installation for uninstallation jobs at this point
            // This is because in Odoo when closing an uninstallation job, the state of the job is set to 'done'
            // even if the electrician cannot perform the uninstallation
            // TODO: change this when Odoo is fixed
            if (job!.typeInstallation !== ODOO_INST_TYPE.UNINSTALL) {
                await startInstallation();
                // we need to fetch fresh intervention data
                // as we updated the inspection with deepomatic work order id
                await refreshJobDetails();
            }

            if (
                isCeeEnabled(
                    job!.typeInstallation,
                    job!.organisationalUnit[2].hasCee
                ) &&
                !isSocialHousing(job!.organisationalUnit) &&
                !hasQuoteSignature
            ) {
                router.push(`/${job!.id}/cee-eligibility`);
            } else {
                // We want to reset the stack to the job details page
                router.push(`/${job!.id}`, 'none');
                // Then we want to navigate to the next page with a forward transition
                setTimeout(() => {
                    router.push(
                        `/${job!.id}/${
                            job!.typeInstallation === ODOO_INST_TYPE.UNINSTALL
                                ? 'uninstallation/inspect'
                                : 'installation/inspect'
                        }`,
                        'forward',
                        'pop'
                    );
                }, 0);
            }
        } catch (e) {
            presentToast({
                message: t`Oops, something went wrong while starting installation`,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoading(false);
    }, [
        job,
        hasQuoteSignature,
        startInstallation,
        refreshJobDetails,
        router,
        presentToast,
    ]);

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            try {
                await refresh();
            } catch (e: any) {
                presentToast({
                    message: t`Failed to refresh the page! Please try again.`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            event.detail.complete();
        },
        [presentToast, refresh]
    );

    return (
        <Page
            title={t`Job Description`}
            ionButtons={
                <IonButtons slot="start">
                    <IonBackButton text={t`Back`} />
                </IonButtons>
            }
        >
            <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
            </IonRefresher>
            <LoadableContent value={value}>
                {job ? (
                    <>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Planned date</Trans>:{' '}
                                {`${timestampToTime(
                                    job.dateStartLocal || job.dateStart
                                )} ${timestampToDate(
                                    job.dateStartLocal || job.dateStart
                                )}`}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Customer name</Trans>:{' '}
                                {`${job.customerId[1] || ''}`}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Address</Trans>:{' '}
                                {`${job.street || ''} ${job.number || ''}, ${
                                    job.street2 || ''
                                } ${job.city || ''}, ${job.zip || ''}`}
                            </IonText>
                        </IonItem>
                        {job.mobile && (
                            <IonItem lines="full">
                                <IonText>
                                    <Trans>Mobile number</Trans>:{' '}
                                    <IonIcon icon={call} size="m"></IonIcon>{' '}
                                    <a
                                        href={`tel:${job.mobile}`}
                                    >{`${job.mobile}`}</a>
                                </IonText>
                            </IonItem>
                        )}
                        {job.phone && (
                            <IonItem lines="full">
                                <IonText>
                                    <Trans>Phone number</Trans>:{' '}
                                    <IonIcon icon={call} size="m"></IonIcon>{' '}
                                    <a
                                        href={`tel:${job.phone}`}
                                    >{`${job.phone}`}</a>
                                </IonText>
                            </IonItem>
                        )}
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Heaters to install</Trans>:{' '}
                                {heatersToInstall}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Rooms already installed</Trans>:{' '}
                                {numOfRooms}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Heaters already installed</Trans>:{' '}
                                {numOfHeaters}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Comments from Planner</Trans>:{' '}
                                {job.planningComment || '-'}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Comments from Customer</Trans>:{' '}
                                {job.availableComment || '-'}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Overview</Trans>
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                {job.summary || '-'} <br />
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>{job.kboxSummary || '-'}</IonText>
                        </IonItem>
                        <div className="ion-padding">
                            <LoadingButton
                                loading={loading}
                                onClick={onStartInterventionHandler}
                                data-tiko-test="start-intervention-button"
                            >
                                {job.typeInstallation ===
                                ODOO_INST_TYPE.UNINSTALL ? (
                                    <Trans>Uninstall</Trans>
                                ) : job.state === ODOO_INST_STATES.RUNNING ? (
                                    <Trans>Continue Installation</Trans>
                                ) : (
                                    <Trans>Start Installation</Trans>
                                )}
                            </LoadingButton>
                        </div>
                    </>
                ) : (
                    <p>
                        <Trans>Intervention with id {jobId} not found</Trans>
                    </p>
                )}
            </LoadableContent>
        </Page>
    );
};

export default JobDetails;
